// Multi-unit selection helpers — computes how many physical units a room needs,
// derives tag prefixes per model family, and aggregates units across rooms.
//
// Algorithm (per Vladimir 2026-06-27 — qty=1 by default):
//   1. Select a unit for the given conditions.
//   2. For models other than N-f, try different lengths with max performance.
//   3. ALWAYS default to qty=1. Pick the SHORTEST length that meets target
//      within ±5%; if no length can, pick the LONGEST available length and
//      accept negative Delivered % (undersized — surfaced to the engineer
//      in the schedule).
//   4. The engineer can manually bump qty>1 from the Summary tab when they
//      want a duplex / triplex configuration. The forceQty branch below
//      honors that override exactly (no auto-derivation).
//   5. Plan exposes `units[]` so user can override with same or different
//      models/lengths per sub-unit via the Split flow.

import { isModelN, isModelO2, isPrimaryAir } from "./wizardCalc";

export const CAPACITY_TOLERANCE = 0.05; // ±5%

// Tag prefix mapping per user spec:
//   - Models N, N-f          → EWS  (Energy Wall System)
//   - Models S, O-1, O-2     → FACB (Fan Active Chilled Beam)
//   - Models I-* (Induction) → IU   (Induction Unit)
//   - All others (C, C1, H, L-*) → ACB (Active Chilled Beam)
export function tagPrefixForModel(model) {
  if (!model) return "U";
  if (isModelN(model)) return "EWS";
  if (model === "Model S" || model === "Model S-HW" || model === "Model O-1" || isModelO2(model)) {
    return "FACB";
  }
  if (model.startsWith("Model I-")) return "IU";
  return "ACB";
}

// FACB models use MED speed as the design selection reference (HIGH is reserve).
// All other multi-speed models use HIGH (max performance).
function isFACB(model) {
  return (
    model === "Model S" ||
    model === "Model S-HW" ||
    model === "Model O-1" ||
    isModelO2(model)
  );
}

// Given a per-room raw API result, return the array of available
// (length_ft, max_total_cooling_btuh) pairs sorted by length.
// For Model O-2, `piping` filters to the user-chosen circuit arrangement
// ("serial" or "parallel") so the selection isn't polluted by the
// other-piping rows the backend returns.
export function lengthCapacityList(raw, model, piping, basis = "water_side") {
  if (isModelN(model)) {
    // Single length — Models N / N-f are STRICTLY 3 ft only.
    // Use the air-side total from the response.
    const ai = raw?.summary_table?.air_side || {};
    const total = ai.total_capacity_btuh ?? 0;
    return [{ length_ft: raw?.system_parameters?.length_ft || 3, max_total: total }];
  }
  let arr = raw?.results || [];
  if (isModelO2(model) && piping) {
    const filtered = arr.filter((r) => (r?.cooling?.piping || "parallel") === piping);
    if (filtered.length) arr = filtered;
  }
  const selectSpeed = isFACB(model) ? "med" : "high";
  // Key on (length_ft, circuits) so distinct circuit rows at the same length
  // survive as separate entries. The backend now prunes to one row per length
  // for O-1/O-2/S/S-HW/I-L; for other models both rows can exist.
  const byKey = new Map();
  arr.forEach((r) => {
    const speeds = r?.cooling?.speeds || [];
    // For FACB: prefer MED speed (design reference). Fallback to HIGH then
    // to whatever speed is available. For other models: HIGH.
    const pick =
      speeds.find((s) => s.speed === selectSpeed) ||
      speeds.find((s) => s.speed === "high") ||
      speeds[speeds.length - 1] ||
      {};
    const a = pick.air_side || {};
    const opt = r?.optimal_point || {};
    const isWaterSide = basis === "water_side";
    // For room_load basis, PREFER opt.air_*_btuh — after backend Fix 1,
    // opt.air_*_btuh on optimal_point = combined PA+coil room-delivered Q_r
    // (set by server.py ~lines 3613 and 3963). opt.final_*_btuh = water-side
    // Q_c, which is wrong for length selection on PA models.
    // For water_side basis, prefer speed-row final_*_btuh (direct coil pick
    // at that length's water ceiling) over optimal_point values.
    const max_total = isWaterSide
      ? (pick.final_total_btuh ?? a.air_total_btuh ?? opt.final_total_btuh ?? opt.air_total_btuh ?? 0)
      : (opt.air_total_btuh ?? opt.final_total_btuh ?? a.air_total_btuh ?? pick.final_total_btuh ?? 0);
    const max_sensible = isWaterSide
      ? (pick.final_sensible_btuh ?? a.air_sensible_btuh ?? opt.final_sensible_btuh ?? opt.air_sensible_btuh ?? 0)
      : (opt.air_sensible_btuh ?? opt.final_sensible_btuh ?? a.air_sensible_btuh ?? pick.final_sensible_btuh ?? 0);
    if (r.length_ft == null || max_total <= 0) return;
    const circuits = r.circuits ?? null;
    const mapKey = `${r.length_ft}__${circuits ?? ""}`;
    const prev = byKey.get(mapKey);
    if (!prev || max_total > prev.max_total)
      byKey.set(mapKey, {
        length_ft: r.length_ft,
        circuits,
        max_total,
        max_sensible,
        nozzle_warning: r.nozzle_warning ?? null,
        circuit_selection: r.circuit_selection ?? null,
      });
  });
  return [...byKey.values()]
    .sort((a, b) => a.length_ft - b.length_ft || (a.circuits ?? 0) - (b.circuits ?? 0));
}

// Pick the shortest length whose capacity can cover the per-unit target.
// `useSensible=true` makes the comparison against `max_sensible` (ACB / IU
// sensible-load products). Fallback to longest if none qualifies.
function pickShortestCompliantLength(lengths, perUnitTarget, useSensible = false) {
  const floor = perUnitTarget * (1 - CAPACITY_TOLERANCE);
  const valueOf = (l) => (useSensible ? l.max_sensible : l.max_total) || 0;
  const picked = lengths.find((l) => valueOf(l) >= floor);
  return picked || lengths[lengths.length - 1];
}

// Compute qty + chosen length for a room using the new tolerance-aware algorithm.
//
// Returns {
//   qty, length_ft, per_unit_total, per_unit_target, per_unit_oa, applied,
//   units: [{ model, length_ft, qty, per_unit_target, per_unit_total, per_unit_oa }],
//   overridden: false,
// }
// `applied` is true when qty > 1 (for UI highlighting).
// `per_unit_oa` divides the room's OA across qty using Math.ceil so
// total OA ≥ target (closest above).
// `piping` is the user-chosen O-2 circuit arrangement; ignored for other models.
export function computeRoomUnitPlan(raw, room, model, piping, maxLenCap = null, forceQty = null, qtyMode = "divide", basis = "water_side") {
  // Target: cooling_total_btuh preferred. Fall back to cooling_sensible_btuh
  // (ACB / IU products are sensible-load focused — users often specify
  // sensible only). When using sensible we compare against `max_sensible`
  // (not `max_total`) so the picker selects the right length.
  //
  // `forceQty` (optional) is set by the Summary tab when the user manually
  // overrides the auto-derived qty. We then split the room target into
  // `forceQty` equal portions and pick the shortest length whose per-unit
  // max ≥ portion. If the longest length still can't cover the per-unit
  // portion the picker falls back to longest (negative Delivered % is then
  // shown in the schedule, signalling the manual choice is undersized).
  const tgtTot = parseFloat(room.cooling_total_btuh) || 0;
  const tgtSen = parseFloat(room.cooling_sensible_btuh) || 0;
  const useSensible = !(tgtTot > 0) && tgtSen > 0;
  const target = useSensible ? tgtSen : tgtTot;
  const valueOf = (l) => (useSensible ? l.max_sensible : l.max_total) || 0;

  const roomOa = parseFloat(room.oa_flow_cfm) || 0;
  const allLengths = lengthCapacityList(raw, model, piping, basis);
  // Apply cap: if cap is set, restrict picker to lengths ≤ cap. If the cap
  // can't cover the target, the picker still falls back to the longest
  // length within cap (still undersized — bump loop will have already done
  // its best in runRoomSelection).
  const lengths = maxLenCap != null
    ? allLengths.filter((l) => l.length_ft <= maxLenCap)
    : allLengths;

  const splitOa = (q) => (q > 0 && roomOa > 0 ? Math.ceil(roomOa / q) : roomOa);

  if (!lengths.length) {
    return {
      qty: 1,
      length_ft: null,
      per_unit_total: 0,
      per_unit_target: target,
      per_unit_oa: roomOa,
      applied: false,
      units: [{ model, length_ft: null, qty: 1, per_unit_target: target, per_unit_total: 0, per_unit_oa: roomOa }],
      overridden: false,
    };
  }

  const longest = lengths[lengths.length - 1];

  if (target <= 0) {
    // No target → shortest length, qty 1
    const first = lengths[0];
    return {
      qty: 1,
      length_ft: first.length_ft,
      per_unit_total: first.max_total,
      per_unit_target: 0,
      per_unit_oa: roomOa,
      applied: false,
      units: [{ model, length_ft: first.length_ft, qty: 1, per_unit_target: 0, per_unit_total: first.max_total, per_unit_oa: roomOa }],
      overridden: false,
    };
  }

  // ---- forceQty override (manual edit from Summary tab) ----
  // Splits the room target into `forceQty` portions and picks the shortest
  // length whose per-unit max ≥ portion. Returns BEFORE the auto qty-derivation
  // branches so user choice is honoured exactly.
  //
  // qtyMode='divide'    (default): the room load is split across QTY units.
  //                                Each unit handles target/qty and oa/qty.
  // qtyMode='duplicate' (option):  each unit handles the FULL room target
  //                                and the FULL room OA. Used when the
  //                                engineer wants multiple identical
  //                                independent units serving the same room
  //                                (e.g. parallel zones, redundancy).
  if (forceQty && forceQty > 0) {
    const duplicate = qtyMode === "duplicate";
    const perUnitTarget = duplicate ? target : target / forceQty;
    const perUnitOa = duplicate ? roomOa : splitOa(forceQty);
    // Pick the shortest length that meets per-unit target. If none does,
    // fall back to longest (undersized — schedule will show negative %).
    let picked = lengths.find((l) => valueOf(l) >= perUnitTarget);
    if (!picked) picked = longest;
    return {
      qty: forceQty,
      length_ft: picked.length_ft,
      per_unit_total: picked.max_total,
      per_unit_target: perUnitTarget,
      per_unit_oa: perUnitOa,
      qty_mode: qtyMode,
      applied: forceQty > 1,
      units: [
        { model, length_ft: picked.length_ft, qty: forceQty, per_unit_target: perUnitTarget, per_unit_total: picked.max_total, per_unit_oa: perUnitOa },
      ],
      overridden: true, // mark so Summary tab can show a small "manual qty" hint
    };
  }

  // qty=1 default — pick the shortest length that meets target within ±5%,
  // OR the longest available length if none of them can (pickShortestCompliantLength
  // already falls back to longest when no length crosses the floor). When the
  // longest is still short, Delivered % goes negative and the schedule shows
  // the undersize so the engineer can manually escalate to qty>1 via the
  // Summary tab if a duplex / triplex makes sense for that room.
  //
  // Removed (2026-06-27): the prior auto-split branch that derived
  // qty = ceil(target / longest.max). Auto-splitting was inferring intent
  // the engineer hadn't expressed — the right default is "one unit, sized
  // up to the longest available length", and the engineer opts into multi-
  // unit configurations explicitly.
  const picked = pickShortestCompliantLength(lengths, target, useSensible);
  return {
    qty: 1,
    length_ft: picked.length_ft,
    per_unit_total: picked.max_total,
    per_unit_target: target,
    per_unit_oa: roomOa,
    applied: false,
    units: [
      { model, length_ft: picked.length_ft, qty: 1, per_unit_target: target, per_unit_total: picked.max_total, per_unit_oa: roomOa },
    ],
    overridden: false,
  };
}

// Aggregate Unit Schedule across all rooms.
// Groups by (unit_model, length_ft) and sums qty.
// Uses plan.units[] when present (so heterogeneous overrides are honored).
// Returns array sorted by tag prefix, then by tag number.
export function buildUnitSchedule(rooms, results) {
  const groups = new Map();

  rooms.forEach((room, i) => {
    const res = results[i];
    if (!res?.ok) return;
    const plan = res.summary?.plan;
    const units =
      plan?.units && plan.units.length
        ? plan.units
        : [
            {
              model: room.unit_model,
              length_ft: plan?.length_ft ?? null,
              qty: plan?.qty ?? 1,
              per_unit_target: plan?.per_unit_target ?? (parseFloat(room.cooling_total_btuh) || 0),
              per_unit_total: plan?.per_unit_total ?? 0,
              // Carry the sub-unit's own result if available
              result: null,
            },
          ];

    const isSingleVariant = units.length === 1;
    const singleVariantQty = plan?.qty ?? units[0]?.qty ?? 1;

    units.forEach((u, uIdx) => {
      const model = u.model || room.unit_model;
      const key = `${model}__${u.length_ft || ""}__${u.circuits ?? ""}`;
      if (!groups.has(key)) {
        // Prefer the sub-unit's own result for per-unit values; else fall back to room result
        const subRes = u.result || res;
        const s = subRes.summary || {};
        const divisor = u.result ? 1 : Math.max(1, plan?.qty || 1);
        // Nozzle fallback: summaries cached BEFORE the May-2026 ordering-
        // codes refactor don't carry `nozzle_label`. Dig it out of the raw
        // result's cooling.speeds[*] if needed so legacy projects still
        // populate the Configurator's auto fields.
        let nozzleLabel = s.nozzle_label || null;
        if (!nozzleLabel) {
          const rawResults = subRes.raw?.results || [];
          const pickedLen = s.selected_length_ft ?? u.length_ft;
          const pickedRow =
            (pickedLen != null && rawResults.find((r) => r.length_ft === pickedLen)) ||
            rawResults[0];
          const speeds = pickedRow?.cooling?.speeds || [];
          const pa = speeds.find((sp) => sp?.speed === "pa");
          const high = speeds.find((sp) => sp?.speed === "high") || speeds[speeds.length - 1];
          nozzleLabel = pa?.nozzle_label || high?.nozzle_label
            || pickedRow?.optimal_point?.nozzle_label || null;
        }
        groups.set(key, {
          unit_model: model,
          length_ft: u.length_ft ?? null,
          circuits: u.circuits ?? null,
          qty: 0,
          rooms: [],
          per_unit: {
            oa_cfm: parseFloat(s.oa_cfm) || parseFloat(room.oa_flow_cfm) || 0,
            total_btuh: u.per_unit_total || parseFloat(s.cooling_total_btuh) || 0,
            sensible_btuh: (parseFloat(s.cooling_sensible_btuh) || 0) / divisor,
            latent_btuh: (parseFloat(s.cooling_latent_btuh) || 0) / divisor,
            heating_btuh: (parseFloat(s.heating_btuh) || 0) / divisor,
            water_flow_gpm: (parseFloat(s.water_flow_gpm) || 0) / divisor,
            water_pd_fth2o: parseFloat(s.water_pd_fth2o) || 0,
            nozzle_label: nozzleLabel,
          },
        });
      }
      const g = groups.get(key);
      g.qty += isSingleVariant ? singleVariantQty : (u.qty || 1);
      g.rooms.push({
        room_id: room.id || null,   // stable UUID (schema v3 primary key)
        room_idx: i,                // positional fallback — kept for compat
        room_name: room.name,
        qty: isSingleVariant ? singleVariantQty : (u.qty || 1),
        sub_unit_idx: uIdx,
      });
    });
  });

  // Assign tags grouped by prefix
  const counters = {};
  const all = [...groups.values()].sort((a, b) => {
    const pa = tagPrefixForModel(a.unit_model);
    const pb = tagPrefixForModel(b.unit_model);
    if (pa !== pb) return pa.localeCompare(pb);
    return (a.length_ft || 0) - (b.length_ft || 0);
  });
  return all.map((g) => {
    const prefix = tagPrefixForModel(g.unit_model);
    counters[prefix] = (counters[prefix] || 0) + 1;
    return { ...g, tag: `${prefix}-${counters[prefix]}` };
  });
}

export { isPrimaryAir, isModelO2 };
